import "./Collaborator.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoSend } from "react-icons/io5";
import { FiMessageSquare, FiPaperclip, FiAtSign, FiDownload, FiUserPlus } from "react-icons/fi";
import { HiUserGroup, HiViewfinderCircle } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import CircularButton from "../ui/CustomButton";
import { MdClose, MdDelete } from "react-icons/md";
import newRequest from "../../utils/newRequest.js";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";
import { showToast } from "../../toastService.js";
import AsyncSelect from "react-select/async";
import { useSocket } from "../../redux/useSocket";
import { customSelectStyles_dark, customSelectStyles_light } from "../ui/CustomSelect";
import { fetchUsersBySearch, logData } from "../../utils/genericFunctions";
import { useSelector } from "react-redux";


// ---------- helpers ----------
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const formatTime = (d) =>
    new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const initialsFromName = (name = "") => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    const a = (parts[0] || "").slice(0, 1);
    const b = (parts[1] || parts[0] || "").slice(0, 1);
    return (a + b).toUpperCase();
};

const isProbablyPdf = (mime = "", name = "") =>
    String(mime).toLowerCase().includes("pdf") || String(name).toLowerCase().endsWith(".pdf");

const isProbablyImage = (mime = "", name = "") => {
    const m = String(mime).toLowerCase();
    const n = String(name).toLowerCase();
    return (
        m.startsWith("image/") ||
        n.endsWith(".png") ||
        n.endsWith(".jpg") ||
        n.endsWith(".jpeg") ||
        n.endsWith(".webp") ||
        n.endsWith(".gif")
    );
};

const isProbablyText = (mime = "", name = "") => {
    const m = String(mime).toLowerCase();
    const n = String(name).toLowerCase();
    return (
        m.startsWith("text/") ||
        n.endsWith(".txt") ||
        n.endsWith(".csv") ||
        n.endsWith(".log") ||
        n.endsWith(".json")
    );
};

// Extract mentions from msg_text and return DB-shaped msg_mentions with start/end.
// Only mentions present in `users` are included.
const extractMentionsWithRanges = (msg_text = "", users = []) => {

    const byUsername = new Map(users.map((u) => [String(u.name), u]));
    const out = [];
    const re = /@([a-zA-Z0-9_]+)/g;

    let m;
    while ((m = re.exec(msg_text)) !== null) {
        const uname = m[1];
        const u = byUsername.get(uname);
        if (!u) continue;

        const start = m.index;
        const end = m.index + m[0].length;

        out.push({
            user_docid: String(u.user_docid),
            username: uname,
            display_name: u.name,
            start,
            end,
        });
    }

    return out;
};

// Render message text with clickable mentions (based on msg_mentions ranges)
const RenderMsgText = ({ msg_text, msg_mentions, onMentionClick }) => {
    if (!msg_text) return null;

    const ranges = (msg_mentions || [])
        .filter((x) => Number.isInteger(x.start) && Number.isInteger(x.end))
        .sort((a, b) => a.start - b.start);

    if (!ranges.length) return <span>{msg_text}</span>;

    const nodes = [];
    let cursor = 0;

    ranges.forEach((r, idx) => {
        const s = Math.max(0, r.start);
        const e = Math.min(msg_text.length, r.end);

        if (s > cursor) {
            nodes.push(
                <span key={`t-${idx}-a`}>
                    {msg_text.slice(cursor, s)}
                </span>
            );
        }

        nodes.push(
            <button
                key={`m-${idx}-${r.user_docid}`}
                type="button"
                className="mention"
                onClick={(e) => {
                    e.stopPropagation();
                    onMentionClick?.(r);
                }}
                title={r.display_name}
            >
                @{r.display_name || r.username}
            </button>
        );

        cursor = e;
    });

    if (cursor < msg_text.length) {
        nodes.push(
            <span key="t-last">
                {msg_text.slice(cursor)}
            </span>
        );
    }

    return <>{nodes}</>;
};


// --- SAFE blob endpoints (protected) ---
const buildInlineUrl = ({ messageId, fileId, storage_key }) => {
    const base = (newRequest?.defaults?.baseURL || "").replace(/\/$/, "");
    if (fileId) return `${base}/collab/message/${messageId}/file/${fileId}/inline`;
    // fallback: storage_key
    return `${base}/collab/message/${messageId}/file/_/inline?storage_key=${encodeURIComponent(storage_key || "")}`;
};

const buildDownloadUrl = ({ messageId, fileId, storage_key }) => {
    const base = (newRequest?.defaults?.baseURL || "").replace(/\/$/, "");
    if (fileId) return `${base}/collab/message/${messageId}/file/${fileId}/download`;
    return `${base}/collab/message/${messageId}/file/_/download?storage_key=${encodeURIComponent(storage_key || "")}`;
};

const isPreviewable = (a = {}) => {
    const mime = String(a?.mime || "").toLowerCase();
    const name = String(a?.name || "").toLowerCase();
    return (
        mime.includes("pdf") ||
        mime.startsWith("image/") ||
        name.endsWith(".pdf") ||
        /\.(png|jpg|jpeg|webp|gif)$/i.test(name)
    );
};

// ---------- CollaboratorPreview (built for this component) ----------
const CollaboratorPreview = ({ open, onClose, url, name, mime, currentTheme }) => {
    if (!open) return null;

    const m = String(mime || "").toLowerCase();
    const isImg = m.startsWith("image/");
    const isPdf = m.includes("pdf");
    const isText = m.startsWith("text/") || name.toLowerCase().match(/\.(txt|csv|log|json)$/);

    return (
        <motion.div className="overlay-dialog" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="overlay-wrapper pdf">
                <div className="collab-overlay-header">
                    <span className="main-title">{name}</span>
                    <CircularButton currentTheme={currentTheme} onClick={onClose} icon={<MdClose />} width="35px" height="35px" />
                </div>
                <div className="collab-overlay-content">
                    {isImg && <img src={url} alt={name} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
                    {isPdf && <iframe title={name} src={url} style={{ width: "100%", height: "100%", border: 0 }} />}
                    {isText && <iframe title={name} src={url} style={{ width: "100%", height: "100%", border: 0 }} />}
                    {!isImg && !isPdf && !isText && (
                        <div style={{ padding: 16 }}>No preview available.</div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};


const Collaborator = ({
    open = false,
    onClose,
    docType,
    docNo,
    docId,
    currentTheme,
    currentUser,
}) => {
    const { t } = useTranslation();
    const socket = useSocket();
    const socketId = useSelector((state) => state.socket.socketId);
    const [tab, setTab] = useState("chat"); // "chat" | "files"  | "team"
    // team
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);


    // composer
    const [text, setText] = useState("");
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionIndex, setMentionIndex] = useState(0);

    // attachments (composer): { tmp_id, name, mime, size_bytes, file }
    const [attachments, setAttachments] = useState([]);
    const textareaRef = useRef(null);

    // thread + items
    const [thread, setThread] = useState(null);
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const chatThreadRef = useRef(null);

    // sending + upload progress
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadCurrentName, setUploadCurrentName] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0); // current file
    const [uploadOverall, setUploadOverall] = useState(0); // overall
    // preview modal
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState("");
    const [previewMime, setPreviewMime] = useState("");


    // --- helpers ---
    const roleLabel = (m) =>
        m?.user_role || m?.role || m?.member_role || m?.usr_role || "Member";

    const nameLabel = (m) =>
        m?.usr_full_name || m?.name || m?.label || m?.usr_name || "User";

    const emailLabel = (m) => m?.usr_email || m?.email || "";

    const photoUrl = (m) => m?.usr_photo_url || m?.photoUrl || m?.avatar || "";


    // fetch team members
    const fetchTeamMembers = useCallback(async () => {
        if (!thread?._id) return;
        setTeamLoading(true);
        try {
            const res = await newRequest.get(`/collab/thread/getMembers/${thread._id}`);
            setTeamMembers(Array.isArray(res?.data?.members) ? res.data.members : []);
        } catch (e) {
            console.error(e);
            setTeamMembers([]);
        } finally {
            setTeamLoading(false);
        }
    }, [thread?._id]);

    const buildMentionUsersFromTeam = (teamMembers = []) => {
        return (teamMembers || [])
            .map((m) => {
                const full = String(m?.usr_full_name || "").trim();
                const email = String(m?.usr_email || "").trim();

                // pick a stable username for @mentions:
                // prefer email local-part, fallback to first name, fallback to user_docid
                // const base =
                //     (email && email.includes("@") ? email.split("@")[0] : "") ||
                //     (full ? full.split(/\s+/)[0] : "") ||
                //     String(m?.user_docid || "");

                const base = full;

                const username = String(base || "")
                    .replace(/[^a-zA-Z0-9_]/g, "_")
                    .slice(0, 24);

                const initials = initialsFromName(full || username);

                return {
                    user_docid: String(m?.user_docid || ""),
                    name: full || username,
                    display_name: full,
                    username,
                    initials,
                    usr_email: email,
                    usr_photo_url: m?.usr_photo_url || "",
                    role: m?.col_role || "Member",
                };
            })
            .filter((u) => u.user_docid && u.username);
    };

    const usersForMentions = useMemo(
        () => buildMentionUsersFromTeam(teamMembers),
        [teamMembers]
    );

    useEffect(() => {
        if (!open) return;
        // load team whenever tab switches to team 
        fetchTeamMembers();
    }, [open, thread?._id, fetchTeamMembers]);

    // add member
    const addTeamMember = async (opt) => {
        try {
            if (!thread?._id) return;
            if (!opt?.value) return;

            // value should be user id from AsyncSelect option
            await newRequest.post(`/collab/thread/addMember/${thread._id}`, {
                user_docid: String(opt.value),
                role: "Member", // or let user pick
                docType,
                docNo,
                docId,
            });

            setSelectedMember(null);
            await fetchTeamMembers();
            showToast("Member added", "success");
        } catch (e) {
            console.error(e);
            showToast("Failed to add member", "error");
        }
    };

    // Remove member 
    const removeTeamMember = async (member) => {
        try {
            if (!thread?._id) return;
            const id = String(member?.user_docid || member?._id || member?.usr_id || "");
            if (!id) return;

            await newRequest.delete(`/collab/thread/removeMember/${thread._id}/${id}`);

            await fetchTeamMembers();
            showToast("Member removed", "success");
        } catch (e) {
            console.error(e);
            showToast("Failed to remove member", "error");
        }
    };

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            const el = chatThreadRef.current;
            if (!el) return;
            el.scrollTop = el.scrollHeight;
        });
    }, []);

    const fetchThreadAndMessages = useCallback(async () => {
        if (!open) return;
        if (!docType || !docNo || !docId) return;

        setLoading(true);
        try {
            const r1 = await newRequest.post("/collab/thread/addCollab", {
                col_doc_type: docType,
                col_doc_no: docNo,
                col_doc_id: docId,
            });

            const th = r1?.data?.thread;
            setThread(th);

            const r2 = await newRequest.get(`/collab/thread/getMessages/${th._id}`, {
                params: { limit: 50 },
            });

            setItems(r2?.data?.items || []);
            setNextCursor(r2?.data?.next_cursor || null);

            scrollToBottom();
        } catch (e) {
            console.error("collab init error:", e);
        } finally {
            setLoading(false);
        }
    }, [open, docType, docNo, docId, scrollToBottom]);

    const loadOlder = useCallback(async () => {
        if (!thread?._id) return;
        if (!nextCursor) return;
        if (loadingMore) return;

        setLoadingMore(true);
        try {
            const r = await newRequest.get(`/collab/thread/getMessages/${thread._id}`, {
                params: { limit: 50, cursor: nextCursor },
            });

            setItems((prev) => [...(r?.data?.items || []), ...prev]);
            setNextCursor(r?.data?.next_cursor || null);
        } catch (e) {
            console.error("load older error:", e);
        } finally {
            setLoadingMore(false);
        }
    }, [thread?._id, nextCursor, loadingMore]);

    useEffect(() => {
        if (!open) return;

        setItems([]);
        setThread(null);
        setNextCursor(null);

        fetchThreadAndMessages();
    }, [open, docType, docNo, docId, fetchThreadAndMessages]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    const onThreadScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollTop <= 0 && nextCursor && !loadingMore) loadOlder();
    };

    const filteredUsers = useMemo(() => {
        const q = (mentionQuery || "").toLowerCase();
        return usersForMentions
            .filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
            .slice(0, 6);
    }, [mentionQuery, usersForMentions]);

    useEffect(() => setMentionIndex(0), [filteredUsers.length, mentionQuery]);

    const onTextChange = (v) => {
        setText(v);

        // auto-resize (grow, no textarea scrollbar)
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 220)}px`;
        });

        const m = v.match(/(?:^|\s)@([a-zA-Z0-9_]{0,24})$/);
        if (m) {
            setShowMentions(true);
            setMentionQuery(m[1] || "");
            setMentionIndex(0);
        } else {
            setShowMentions(false);
            setMentionQuery("");
        }
    };

    const insertMention = (user) => {
        setText((prev) =>
            prev.replace(/@\w*$/, `@${user.username} `)
        );

        setShowMentions(false);
        setMentionQuery("");
        setMentionIndex(0);

        requestAnimationFrame(() => textareaRef.current?.focus());
    };

    const onPickFiles = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setAttachments((prev) => [
            ...prev,
            ...files.map((f) => ({
                tmp_id: uuidv4(),
                name: f.name,
                mime: f.type || "application/octet-stream",
                size_bytes: f.size,
                file: f,
            })),
        ]);

        e.target.value = "";
    };

    const removeAttachment = (tmp_id) => {
        setAttachments((prev) => prev.filter((a) => a.tmp_id !== tmp_id));
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 220)}px`;
        });
    }, [attachments.length]);

    const handleKeyDown = (e) => {
        if (showMentions && filteredUsers.length > 0) {
            if (e.key === "Enter") {
                e.preventDefault();
                insertMention(filteredUsers[mentionIndex] || filteredUsers[0]);
                return;
            }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIndex((i) => Math.max(i - 1, 0));
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setShowMentions(false);
                return;
            }
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);



    // -------- preview + download ----------
    const openPreview = async (messageId, a) => {
        try {
            if (!messageId) return showToast("Missing message id", "error");
            if (!a) return showToast("Missing attachment", "error");

            if (!isPreviewable(a)) {
                showToast("This file can't be previewed. Use download.", "warning");
                return;
            }

            const fileId = a?.file_docid ? String(a.file_docid) : null;
            const url = buildInlineUrl({ messageId, fileId, storage_key: a?.storage_key });
            // console.log(`openPreview url ... ${url}`);
            const res = await newRequest.get(url, { responseType: "blob" });
            const blob = res.data;

            if (previewUrl) URL.revokeObjectURL(previewUrl);
            const objUrl = URL.createObjectURL(blob);

            setPreviewUrl(objUrl);
            setPreviewName(a?.name || "Preview");
            setPreviewMime(a?.mime || blob.type || "");
            setPreviewOpen(true);
        } catch (err) {
            console.error(err);
            showToast("Preview failed (file not found or no access).", "error");
        }
    };

    const downloadAttachment = async (messageId, a) => {
        try {
            if (!messageId) return showToast("Missing message id", "error");
            if (!a) return showToast("Missing attachment", "error");

            const fileId = a?.file_docid ? String(a.file_docid) : null;
            const url = buildDownloadUrl({ messageId, fileId, storage_key: a?.storage_key });

            // console.log(`downloadAttachment url ... ${url}`);

            const res = await newRequest.get(url, { responseType: "blob" });
            const blob = res.data;

            const objUrl = URL.createObjectURL(blob);
            const filename = a?.name || "file";

            const link = document.createElement("a");
            link.href = objUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(objUrl);
        } catch (err) {
            console.error(err);
            showToast("Download failed (file not found or no access).", "error");
        }
    };

    // ---------- upload ----------
    const uploadOneToBackend = async ({ fileObj, folder }) => {
        const formData = new FormData();

        formData.append("folder", folder);
        formData.append("documentType", docType);
        formData.append("documentNo", docNo);
        formData.append("documentId", docId);

        formData.append("file", fileObj.file); //IMPORTANT. File Must be last to add

        const res = await newRequest.post("/uploadFiles/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
                if (!evt.total) return;
                const pct = Math.round((evt.loaded / evt.total) * 100);
                setUploadProgress(pct);
            },
        });

        const f = res?.data?.file;
        if (!f?._id) throw new Error("Upload failed: missing file doc");

        // Map AllFile -> msg_attachments schema (DB field names)
        return {
            file_docid: String(f._id),
            name: f.fl_name,
            mime: fileObj.mime || (f.fl_type ? `application/${f.fl_type}` : "application/octet-stream"),
            size_bytes: Math.round((f.fl_size_kb || 0) * 1024),
            storage_key: f.fl_path, // e.g. "/uploads/org/folder/file.ext"
        };
    };

    const uploadAttachmentsForMessage = async () => {
        if (!attachments.length) return [];

        const folder = `collab/${docType}/${docId}`;
        const uploaded = [];

        setUploading(true);
        setUploadOverall(0);

        try {
            const total = attachments.length;

            for (let i = 0; i < total; i++) {
                const a = attachments[i];
                setUploadCurrentName(a.name);
                setUploadProgress(0);

                const up = await uploadOneToBackend({ fileObj: a, folder });
                uploaded.push(up);

                // overall = completed files proportion + current file (approx)
                const donePct = Math.round(((i + 1) / total) * 100);
                setUploadOverall(donePct);
            }

            return uploaded;
        } finally {
            setUploading(false);
            setUploadCurrentName("");
            setUploadProgress(0);
            setUploadOverall(0);
        }
    };

    // ---------- helpers (drop-in) ----------
    const uniqByIdOrClient = (arr = []) => {
        const seen = new Set();
        const out = [];
        for (const m of arr) {
            const k = m?._id
                ? `id:${String(m._id)}`
                : m?.client_msg_id
                    ? `cid:${String(m.client_msg_id)}`
                    : null;

            if (!k) {
                out.push(m);
                continue;
            }
            if (seen.has(k)) continue;
            seen.add(k);
            out.push(m);
        }
        return out;
    };


    // Optional: if you also want to update an optimistic message with attachments *before* POST returns
    const patchOptimisticAttachments = (prev = [], client_msg_id, msg_attachments = []) => {
        const idx = prev.findIndex(
            (x) => x?._optimistic && String(x?.client_msg_id) === String(client_msg_id)
        );
        if (idx < 0) return prev;

        const next = prev.slice();
        next[idx] = { ...next[idx], msg_attachments, _has_local_attachments: true };
        return next;
    };


    // ---------- send message ----------
    const sendMessage = async () => {
        if (!thread?._id) return;
        if (sending || uploading) return;

        const cleanText = (text || "").trim();
        if (!cleanText && attachments.length === 0) return;

        const client_msg_id = uuidv4();
        const msg_mentions = extractMentionsWithRanges(cleanText, usersForMentions);

        const optimistic = {
            type: "message",
            _id: `tmp_${Date.now()}`,
            client_msg_id,
            _optimistic: true,

            col_org_code: currentUser?.usr_org_code,
            thread_id: String(thread._id),

            msg_type: "message",
            msg_text: cleanText,
            msg_mentions,
            msg_attachments: [],

            msg_by_user_docid: String(currentUser?._id || ""),
            usr_full_name: currentUser?.usr_full_name,
            usr_photo_url: currentUser?.usr_photo_url,
            usr_email: currentUser?.usr_email,
            msg_at: new Date().toISOString(),
        };

        const restoreText = cleanText;

        // Optimistic append
        setItems((prev) => uniqByIdOrClient([...prev, optimistic]));

        // Clear composer immediately
        setText("");
        setAttachments([]);
        setShowMentions(false);
        setMentionQuery("");
        setMentionIndex(0);

        requestAnimationFrame(() => {
            if (textareaRef.current) textareaRef.current.style.height = "auto";
            scrollToBottom();
        });

        setSending(true);

        try {
            // 1) upload attachments (if any)
            const uploadedAttachments = await uploadAttachmentsForMessage();

            // show the uploaded attachments immediately on the optimistic bubble
            if (uploadedAttachments?.length) {
                setItems((prev) => patchOptimisticAttachments(prev, client_msg_id, uploadedAttachments));
            }

            // 2) create message (DB fields ONLY)
            const res = await newRequest.post(`/collab/thread/addMessage/${thread._id}`, {
                msg_text: cleanText,
                msg_mentions,
                msg_attachments: uploadedAttachments,
                client_msg_id,
            });

            const real = res?.data?.message;
            if (!real) throw new Error("No message returned");

            // Replace/insert without dup + ensure UI has user details
            setItems((prev) => upsertMessageNoDup(prev, real));
            requestAnimationFrame(scrollToBottom);
        } catch (err) {
            console.error(err);

            // remove only this optimistic
            setItems((prev) =>
                prev.filter((x) => !(x?._optimistic && String(x?.client_msg_id) === String(client_msg_id)))
            );

            // restore text
            setText(restoreText);
            showToast("Failed to send message", "error");
        } finally {
            setSending(false);
        }
    };

    const onMentionClick = (mention) => {
        // mention = { user_docid, username, start, end }
        // replace with open user profile drawer, etc.
        console.log("Open profile:", mention);
    };

    // ---------- UI ----------
    const headerMeta = useMemo(
        () => ({
            no: docNo || "-",
        }),
        [docNo]
    );

    // ---------- Files tab: collect all attachments across currently loaded items ----------
    const allFiles = useMemo(() => {
        const map = new Map();

        for (const it of items || []) {
            if (it?.type !== "message") continue;

            const messageId = String(it._id || "");
            if (!messageId) continue;

            const arr = Array.isArray(it.msg_attachments) ? it.msg_attachments : [];
            for (const a of arr) {
                const fileKey = String(a?.file_docid || a?.storage_key || `${messageId}:${a?.name || "file"}`);
                if (map.has(fileKey)) continue;

                map.set(fileKey, {
                    ...a,
                    _file_key: fileKey,
                    _message_id: messageId,
                    _msg_at: it.msg_at,
                    _usr_full_name: it.usr_full_name,
                });
            }
        }

        return Array.from(map.values()).sort(
            (x, y) => new Date(y._msg_at || 0) - new Date(x._msg_at || 0)
        );
    }, [items]);

    const isNearBottom = () => {
        const el = chatThreadRef.current;
        if (!el) return true;
        return el.scrollHeight - (el.scrollTop + el.clientHeight) < 120;
    };

    // Build a lookup from teamMembers by user_docid
    const teamIndex = useMemo(() => {
        const m = new Map();
        for (const tm of teamMembers || []) {
            const id = String(tm?.user_docid || "");
            if (id) m.set(id, tm);
        }
        return m;
    }, [teamMembers]);

    const enrichMessageForUI = useCallback((msg) => {
        if (!msg) return msg;

        const byId = String(msg.msg_by_user_docid || "");
        const tm = byId ? teamIndex.get(byId) : null;

        // If backend didn't attach user fields, patch from team members
        const patched = {
            ...msg,
            usr_full_name:
                msg.usr_full_name || tm?.usr_full_name || (byId === String(currentUser?._id) ? currentUser?.usr_full_name : "") || msg.usr_full_name,
            usr_photo_url:
                msg.usr_photo_url || tm?.usr_photo_url || (byId === String(currentUser?._id) ? currentUser?.usr_photo_url : "") || msg.usr_photo_url,
            usr_email:
                msg.usr_email || tm?.usr_email || (byId === String(currentUser?._id) ? currentUser?.usr_email : "") || msg.usr_email,
        };

        return patched;
    }, [teamIndex, currentUser]);

    const sameAttachmentsSignature = (a = [], b = []) => {
        const sig = (arr) =>
            (arr || [])
                .map((x) => String(x?.file_docid || x?.storage_key || x?.name || ""))
                .filter(Boolean)
                .join("|");
        return sig(a) === sig(b);
    };

    // Stronger upsert that:
    // - replaces optimistic by client_msg_id when possible
    // - if socket message arrives before POST response (and client_msg_id missing), tries a safe heuristic match
    // - prevents duplicates by _id and by client_msg_id
    const upsertMessageNoDup = useCallback((prev = [], incomingRaw) => {
        if (!incomingRaw) return prev;

        const incoming = enrichMessageForUI(incomingRaw);

        const incomingId = incoming?._id ? String(incoming._id) : "";
        const incomingCid = incoming?.client_msg_id ? String(incoming.client_msg_id) : "";

        let next = prev.slice();

        // 1) Replace optimistic if client_msg_id matches
        if (incomingCid) {
            const optIdx = next.findIndex(
                (x) => x?._optimistic && String(x?.client_msg_id || "") === incomingCid
            );
            if (optIdx >= 0) next[optIdx] = incoming;
        }

        // 2) If no client_msg_id match happened, but incoming has _id and exists, replace it (to upgrade missing fields)
        if (incomingId) {
            const existingIdx = next.findIndex((x) => String(x?._id || "") === incomingId);
            if (existingIdx >= 0) {
                next[existingIdx] = { ...next[existingIdx], ...incoming };
            } else {
                // 3) If it doesn't exist, attempt heuristic match to an optimistic message (sender-side duplication fix)
                // Only do this if incoming is from current user and there's exactly one likely optimistic match.
                const isFromMe = String(incoming?.msg_by_user_docid || "") === String(currentUser?._id || "");
                if (isFromMe) {
                    const candidates = next
                        .map((x, idx) => ({ x, idx }))
                        .filter(({ x }) => x?._optimistic)
                        .filter(({ x }) => (x?.msg_text || "") === (incoming?.msg_text || ""))
                        .filter(({ x }) => sameAttachmentsSignature(x?.msg_attachments, incoming?.msg_attachments))
                        .filter(({ x }) => {
                            // within 30s window
                            const t1 = new Date(x?.msg_at || 0).getTime();
                            const t2 = new Date(incoming?.msg_at || 0).getTime();
                            return Math.abs(t2 - t1) <= 30000;
                        });

                    if (candidates.length === 1) {
                        next[candidates[0].idx] = incoming; // replace optimistic
                    } else {
                        next.push(incoming); // no safe match -> append
                    }
                } else {
                    next.push(incoming);
                }
            }
        } else {
            // incoming without _id (should be rare) -> append if it doesn't duplicate by client_msg_id
            if (incomingCid) {
                const existsCid = next.some((x) => String(x?.client_msg_id || "") === incomingCid);
                if (!existsCid) next.push(incoming);
            } else {
                next.push(incoming);
            }
        }

        // 4) Dedupe pass (keep first occurrence; upgrade optimistic -> real where possible)
        const seenId = new Set();
        const seenCid = new Set();
        const out = [];

        for (const m of next) {
            const id = m?._id ? String(m._id) : "";
            const cid = m?.client_msg_id ? String(m.client_msg_id) : "";

            // If we already have this real _id, skip duplicates
            if (id) {
                if (seenId.has(id)) continue;
                seenId.add(id);
            }

            // If we already have this client_msg_id, skip duplicates
            if (cid) {
                if (seenCid.has(cid)) continue;
                seenCid.add(cid);
            }

            out.push(m);
        }

        return out;
    }, [enrichMessageForUI, currentUser]);


    useEffect(() => {
        if (!socket) return;

        const onNewMessage = ({ threadId, message }) => {
            if (!message) return;
            if (!thread?._id) return;
            if (String(threadId) !== String(thread._id)) return;

            const shouldScroll = isNearBottom?.() ?? true;

            setItems((prev) => upsertMessageNoDup(prev, message));

            if (shouldScroll) requestAnimationFrame(scrollToBottom);
        };

        socket.on("collab:newMessage", onNewMessage);
        return () => socket.off("collab:newMessage", onNewMessage);
    }, [socket, thread?._id, scrollToBottom, isNearBottom, upsertMessageNoDup]);

    // socket listening to incoming messages
    useEffect(() => {
        if (!socket) return;
        if (!thread?._id) return;
        if (!currentUser?.usr_org_code) return;

        const payload = {
            orgCode: currentUser.usr_org_code,
            threadId: String(thread._id),
        };

        socket.emit("collab:join", payload);

        return () => {
            socket.emit("collab:leave", payload);
        };
    }, [socket, thread?._id, currentUser?.usr_org_code]);


    return (
        <>
            <div className={`collab-backdrop ${open ? "open" : ""}`} onClick={() => {
                console.log(`closing backgrop`);
                onClose();
            }} />

            <aside
                className={`collab-drawer ${open ? "open" : ""}`}
                data-theme={currentTheme}
                aria-hidden={!open}
            >
                <div className="collab-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="collab-header">
                        <div className="collab-cols">
                            <div className="collab-header-left">
                                <div className="collab-title">{t("collaborator")}</div>
                                <div className="collab-sub">
                                    <span className="mono">{headerMeta.no}</span>
                                </div>
                            </div>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={onClose}
                                icon={<MdClose color="teal" />}
                                width={"30px"}
                                height={"30px"}
                                addBorder={false}
                            />
                        </div>
                        <div className="segmented">
                            <button
                                type="button"
                                className={`seg ${tab === "chat" ? "active" : ""}`}
                                onClick={() => setTab("chat")}
                            >
                                <FiMessageSquare />
                                <span>{t("chat")}</span>
                            </button>

                            <button
                                type="button"
                                className={`seg ${tab === "files" ? "active" : ""}`}
                                onClick={() => setTab("files")}
                            >
                                <FiPaperclip />
                                <span>{t("files")}</span>
                            </button>
                            <button
                                type="button"
                                className={`seg ${tab === "team" ? "active" : ""}`}
                                onClick={() => setTab("team")}
                            >
                                <HiUserGroup />
                                <span>{t("team")}</span>
                            </button>
                        </div>
                    </div>

                    <div className="collab-body">
                        {tab === "chat" &&
                            <div className="chat-wrap">
                                <div className="chat-thread" ref={chatThreadRef} onScroll={onThreadScroll}>
                                    {loading ? (
                                        <div style={{ padding: 12 }}>{t("loading")}...</div>
                                    ) : (
                                        <>
                                            {loadingMore && (
                                                <div style={{ padding: 8, textAlign: "center" }}>
                                                    {t("loading")}...
                                                </div>
                                            )}

                                            {items.map((it) => {
                                                if (it.type === "date") {
                                                    return (
                                                        <div key={`d-${it.key}`} className="chat-date">
                                                            {it.label}
                                                        </div>
                                                    );
                                                }

                                                const isMe =
                                                    currentUser?._id &&
                                                    it.msg_by_user_docid &&
                                                    String(it.msg_by_user_docid) === String(currentUser._id);

                                                const displayName = isMe ? "You" : it?.usr_full_name || "User";
                                                const displayTime = it.msg_at ? formatTime(it.msg_at) : "";
                                                const avatarText = isMe ? "" : initialsFromName(displayName);

                                                return (
                                                    <div key={it._id} className={`msg ${isMe ? "me" : "other"}`}>
                                                        {!isMe && <div className="avatar">{avatarText}</div>}

                                                        <div className="msg-col">
                                                            <div className="msg-head">
                                                                <div className="msg-name">{displayName}</div>
                                                                <div className="msg-time">{displayTime}</div>
                                                            </div>

                                                            <div className={`bubble ${isMe ? "me" : "other"}`}>
                                                                <div className="bubble-text">
                                                                    <RenderMsgText
                                                                        msg_text={it.msg_text}
                                                                        msg_mentions={it.msg_mentions}
                                                                        onMentionClick={onMentionClick}
                                                                    />
                                                                </div>

                                                                {Array.isArray(it.msg_attachments) && it.msg_attachments.length > 0 && (
                                                                    <div className="bubble-files">
                                                                        {it.msg_attachments.map((a, i) => {
                                                                            const canPreview =
                                                                                isProbablyPdf(a.mime, a.name) ||
                                                                                isProbablyImage(a.mime, a.name) ||
                                                                                isProbablyText(a.mime, a.name);

                                                                            return (
                                                                                <div className="file-card" key={`${it._id}-a-${i}`}>
                                                                                    <div className="file-badge">
                                                                                        {(a.mime || a.name || "")
                                                                                            .split("/")
                                                                                            .pop()
                                                                                            ?.toUpperCase()
                                                                                            ?.slice(0, 3) || "FILE"}
                                                                                    </div>

                                                                                    <div className="file-meta">
                                                                                        <div className="file-name">{a.name}</div>
                                                                                        <div className="file-size">
                                                                                            {typeof a.size_bytes === "number"
                                                                                                ? `${Math.round(a.size_bytes / 1024)} KB`
                                                                                                : ""}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="file-actions">
                                                                                        {isPreviewable(a) ?
                                                                                            <button
                                                                                                className="file-action"
                                                                                                type="button"
                                                                                                onClick={() => openPreview(String(it._id), a)}
                                                                                                disabled={!canPreview}
                                                                                                title={!canPreview ? "Not previewable" : t("preview")}
                                                                                            >
                                                                                                < HiViewfinderCircle />
                                                                                            </button> : <span />}

                                                                                        <button
                                                                                            className="file-action download"
                                                                                            type="button"
                                                                                            onClick={() => downloadAttachment(String(it._id), a)}
                                                                                            title={t("download")}
                                                                                        >
                                                                                            <FiDownload />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>

                                <div className="chat-input">
                                    <div className="composer">
                                        <textarea
                                            ref={textareaRef}
                                            value={text}
                                            onChange={(e) => onTextChange(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            rows={2}
                                            placeholder="Type a message or use @ to mention"
                                        />

                                        {attachments.length > 0 && (
                                            <div className="attach-row">
                                                {attachments.map((a) => (
                                                    <div className="chip" key={a.tmp_id}>
                                                        <span>{a.name}</span>
                                                        <span className="x" onClick={() => removeAttachment(a.tmp_id)}>
                                                            ×
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {(uploading || uploadOverall > 0) && (
                                            <div className="upload-progress">
                                                <div className="up-meta">
                                                    <span className="nm">
                                                        {uploading ? `Uploading: ${uploadCurrentName}` : "Uploading..."}
                                                    </span>
                                                    <span className="pct">{uploadOverall || uploadProgress}%</span>
                                                </div>
                                                <div className="up-bar">
                                                    <div
                                                        className="fill"
                                                        style={{ width: `${uploadOverall || uploadProgress}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="composer-actions">
                                            <div className="left">
                                                <label className="icon-btn" title="Attach">
                                                    <FiPaperclip />
                                                    <input type="file" multiple onChange={onPickFiles} />
                                                </label>

                                                <button
                                                    type="button"
                                                    className="icon-btn"
                                                    title="Mention"
                                                    onClick={() => {
                                                        setText((p) =>
                                                            p.endsWith("@") || p.endsWith("@ ")
                                                                ? p
                                                                : `${p}${p && !p.endsWith(" ") ? " " : ""}@`
                                                        );
                                                        setShowMentions(true);
                                                        setMentionQuery("");
                                                    }}
                                                >
                                                    <FiAtSign />
                                                </button>
                                            </div>

                                            <button
                                                type="button"
                                                className="send"
                                                onClick={sendMessage}
                                                disabled={sending || uploading}
                                                title={
                                                    uploading
                                                        ? `Uploading ${uploadCurrentName} (${uploadOverall || uploadProgress}%)`
                                                        : "Send"
                                                }
                                            >
                                                <IoSend />
                                            </button>
                                        </div>
                                    </div>

                                    {showMentions && filteredUsers.length > 0 && (
                                        <div className="mention-pop">
                                            {filteredUsers.map((u, i) => (
                                                <div
                                                    key={u.user_docid}
                                                    className={`mi ${i === mentionIndex ? "active" : ""}`}
                                                    onClick={() => insertMention(u)}
                                                    onMouseDown={(ev) => {
                                                        ev.preventDefault();
                                                        insertMention(u);
                                                    }}
                                                >
                                                    <div className="av">{u.initials}</div>
                                                    <div className="nm">{u.name}</div>
                                                    <div className="un">@{u.username}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>}

                        {tab === "files" &&
                            <div className="files-wrap">
                                {allFiles.length === 0 ? (
                                    <div style={{ padding: 12 }}>{t("files")}…</div>
                                ) : (
                                    <div className="files-list">
                                        {allFiles.map((a) => {
                                            const canPreview = isProbablyPdf(a.mime, a.name) || isProbablyImage(a.mime, a.name) || isProbablyText(a.mime, a.name);
                                            const who = a._usr_full_name || "User";
                                            const when = a._msg_at ? formatTime(a._msg_at) : "";

                                            return (
                                                <div key={a._file_key} className="file-item">
                                                    <div className="file-icon">
                                                        {(a.mime || a.name || "").split("/").pop()?.toUpperCase()?.slice(0, 3) || "FILE"}
                                                    </div>

                                                    <div className="file-meta">
                                                        <div className="file-name">{a.name}</div>
                                                        <div className="file-size">
                                                            {typeof a.size_bytes === "number" ? `${Math.round(a.size_bytes / 1024)} KB` : ""}{" "}
                                                            {who ? `• ${who}` : ""} {when ? `• ${when}` : ""}
                                                        </div>
                                                    </div>

                                                    <div className="file-actions">
                                                        {isPreviewable(a) ?
                                                            <button
                                                                className="file-action"
                                                                type="button"
                                                                onClick={() => openPreview(String(a._message_id), a)}
                                                                disabled={!canPreview}
                                                                title={!canPreview ? "Not previewable" : t("preview")}
                                                            >
                                                                < HiViewfinderCircle />
                                                            </button> : <span />}

                                                        <button type="button" className="file-action download"
                                                            onClick={() => downloadAttachment(String(a._message_id), a)} >
                                                            <FiDownload />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>}
                        {tab === "team" &&
                            <div className="members-wrap">
                                <div className="members-head">
                                    <AsyncSelect
                                        id="member"
                                        name="member"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                        value={selectedMember}
                                        onChange={(opt) => {
                                            setSelectedMember(opt);
                                            // add immediately after selection:
                                            addTeamMember(opt);
                                        }}
                                        loadOptions={(inputValue, callback) =>
                                            fetchUsersBySearch(inputValue, callback, ["Internal"])
                                        }
                                        width={"90%"}
                                        isSearchable
                                        placeholder={t("team")}
                                        noOptionsMessage={() => "Type Minimum 2 Letter to Search"}
                                    />
                                </div>

                                {teamLoading ? (
                                    <div style={{ padding: 12 }}>{t("loading")}...</div>
                                ) : teamMembers.length === 0 ? (
                                    <div style={{ padding: 12 }}>{t("noMembers") || "No team members yet."}</div>
                                ) : (
                                    <div className="members-list">
                                        {teamMembers.map((m, idx) => {
                                            const nm = nameLabel(m);
                                            const em = emailLabel(m);
                                            const rl = roleLabel(m);
                                            const img = photoUrl(m);

                                            return (
                                                <div className="member-row" key={m.user_docid || m._id || idx}>
                                                    <div className="member-left">
                                                        {img ? (
                                                            <img className="member-avatar-img" src={img} alt={nm} />
                                                        ) : (
                                                            <div className="member-avatar">{initialsFromName(nm)}</div>
                                                        )}

                                                        <div className="member-meta">
                                                            <div className="member-name">{nm}</div>
                                                            <div className="member-sub">
                                                                <span className="member-role">{rl}</span>
                                                                {em ? <span className="dot">•</span> : null}
                                                                {em ? <span className="member-email">{em}</span> : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {m?.user_role !== "Owner" &&
                                                        <div className="member-actions">
                                                            <CircularButton
                                                                currentTheme={currentTheme}
                                                                onClick={() => removeTeamMember(m)}
                                                                color="#E53935"
                                                                icon={<MdDelete />}
                                                                width={"30px"}
                                                                height={"30px"} />
                                                        </div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        }
                    </div>
                </div>
            </aside>
            <CollaboratorPreview
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                url={previewUrl}
                name={previewName}
                mime={previewMime}
                currentTheme={currentTheme}
            />

        </>
    );
};

export default Collaborator;
